import { notification } from 'antd';
import { handleFailedExportNotification } from 'components/Search/Results/Actions/notifications';
import { usePost } from 'hooks';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  getActiveList,
  getAreAllMembersSelected,
  getExportExtraParams,
  getSelectedMemberIds,
  getSelectedMembers,
} from 'selectors/lists';
import { exportMembers } from 'services/api';
import { views } from '../constants';

function useExport(view = views.researchers, fullList = false) {
  const activeList = useSelector(getActiveList);
  const selectedMemberIds = useSelector(getSelectedMemberIds);
  const selectedMembers = useSelector(getSelectedMembers);
  const allSelected = useSelector(getAreAllMembersSelected);
  const extraParams = useSelector(getExportExtraParams);

  const getExportBody = useCallback(
    params => {
      if (fullList || allSelected) {
        return {
          listId: fullList && params?.listId ? params.listId : activeList?.id,
          ...(params ?? extraParams ?? {}),
        };
      }

      if (selectedMembers.all) {
        return {
          listId: activeList?.id,
          excludedFromAllMemberIds: Object.keys(selectedMembers.exclude).map(
            x => +x
          ),
          excludedFromAllResearcherIds: Object.keys(selectedMembers.exclude),
          ...(params ?? extraParams ?? {}),
        };
      }

      return {
        listId: activeList?.id,
        researcherIds: selectedMemberIds,
        memberIds: selectedMemberIds,
        ...(params ?? extraParams ?? {}),
      };
    },
    [
      activeList?.id,
      allSelected,
      extraParams,
      fullList,
      selectedMemberIds,
      selectedMembers.all,
      selectedMembers.exclude,
    ]
  );

  const [{ res: exportRes, error }, postExport] = usePost({
    url: exportMembers(activeList?.id, view),
  });
  useEffect(() => {
    if (exportRes) {
      notification.open({
        message: 'Generating Export',
        description:
          'We are generating your list export and will notify you when it is ready.',
        placement: 'bottomLeft',
      });
    }
  }, [exportRes]);

  useEffect(() => {
    if (error) {
      handleFailedExportNotification(error.status);
    }
  }, [error]);

  const handleExport = useCallback(
    params => {
      const body = getExportBody(params);
      if (fullList && params.listId) {
        postExport({
          url: exportMembers(params.listId, view),
          body,
        });
      } else {
        postExport(body);
      }
    },
    [getExportBody, postExport, fullList, view]
  );

  return [getExportBody, handleExport];
}

export default useExport;
